/**
 * Private chat attachments: from the URL a message carries to one that opens
 * (VAPT 2026-10).
 *
 * A file sent in chat is stored under `private/` when the backend has
 * CHAT_ATTACHMENTS_PRIVATE on. The message still carries a plain CDN URL —
 * `…/private/chat/<id>`, or `…/private/rooms/<room>/<id>` for one sent in a
 * meeting's chat — but the CDN refuses it unsigned: it names the file, it does
 * not open it. Shown, played or downloaded, it is first traded here for a
 * link the backend signs for whoever may see it, valid a few minutes:
 *
 *   chat   GET /api/v1/attachments/<id>/url (app token): its uploader, or a
 *          member of a conversation carrying it
 *   room   GET /api/v1/meeting-chat/<room>/attachments/<id>/url (room token)
 *          while in that meeting; afterwards, with the meeting's id,
 *          GET /api/v1/meetings/<meeting>/chat/attachments/<id>/url (host or
 *          attendee)
 *
 * Every other URL — a public file, one stored before, an outside link — is
 * left as it is. Links are cached until a little before they lapse.
 */

import { config } from '@/config/env'
import { getAppBearer } from '@/lib/sdk-session-token'
import { roomFetch } from '@/features/meeting/services/room-token'
import { useMeetingSessionStore } from '@/features/meeting/store/meeting-session.store'

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'
const CHAT_PATH = new RegExp(`^/private/chat/(${UUID})$`, 'i')
const ROOM_PATH = new RegExp(`^/private/rooms/([0-9a-f-]{8,64})/(${UUID})$`, 'i')

export type PrivateRef = { kind: 'chat'; id: string } | { kind: 'room'; roomId: string; id: string }

/** What a private attachment URL names; null for any other URL. */
export function privateRef(url: string | null | undefined): PrivateRef | null {
  if (!url) return null
  let path: string
  try {
    path = new URL(url).pathname
  } catch {
    return null
  }
  const chat = CHAT_PATH.exec(path)
  if (chat) return { kind: 'chat', id: chat[1] }
  const room = ROOM_PATH.exec(path)
  if (room) return { kind: 'room', roomId: room[1], id: room[2] }
  return null
}

export function isPrivateAttachmentUrl(url: string | null | undefined): boolean {
  return privateRef(url) !== null
}

/** Where the link is asked for: the live meeting (default) or a past one by id. */
export interface ResolveOptions {
  meetingId?: string
}

export interface ResolveDeps {
  /** GET a JSON `{ url, expiresInSec }` with the app token. */
  getWithAppToken?: (path: string) => Promise<Response>
  /** GET with this room's token, or null when not in that room. */
  getWithRoomToken?: (roomId: string, path: string) => Promise<Response> | null
  now?: () => number
  sleep?: (ms: number) => Promise<void>
}

/** A link is replaced this long before it lapses. */
export const RENEW_BEFORE_MS = 30_000
/** Waits before asking again after "not found" for a chat file. */
export const NOT_YET_RETRIES_MS = [1_500, 4_000]

interface Cached {
  url: string
  /** Epoch ms; null for a link that does not lapse. */
  expiresAt: number | null
}

const cache = new Map<string, Cached>()
const inflight = new Map<string, Promise<string>>()

/** Forget every link (sign-out) or one (it failed to load). */
export function forgetResolvedAttachment(url?: string): void {
  if (url === undefined) cache.clear()
  else cache.delete(url)
}

const API = () => (config.apiUrl || '').replace(/\/+$/, '')

const defaultGetWithAppToken = (path: string) =>
  fetch(`${API()}${path}`, { headers: { Authorization: `Bearer ${getAppBearer()}` } })

const defaultGetWithRoomToken = (roomId: string, path: string): Promise<Response> | null => {
  const creds = useMeetingSessionStore.getState().roomCredentials
  const current = creds?.channelTopic?.replace(/^room:/, '')
  if (!creds?.roomToken || current !== roomId) return null
  return roomFetch(roomId, creds.roomToken, `${API()}${path}`)
}

class LinkRefused extends Error {
  readonly status: number
  constructor(status: number) {
    super(`attachment link refused: ${status}`)
    this.status = status
  }
}

async function ask(ref: PrivateRef, opts: ResolveOptions, deps: Required<ResolveDeps>): Promise<Cached> {
  let res: Response | null
  if (ref.kind === 'chat') {
    res = await deps.getWithAppToken(`/api/v1/attachments/${ref.id}/url`)
  } else if (opts.meetingId) {
    res = await deps.getWithAppToken(
      `/api/v1/meetings/${encodeURIComponent(opts.meetingId)}/chat/attachments/${ref.id}/url`,
    )
  } else {
    res = await deps.getWithRoomToken(
      ref.roomId,
      `/api/v1/meeting-chat/${encodeURIComponent(ref.roomId)}/attachments/${ref.id}/url`,
    )
    if (!res) throw new LinkRefused(403)
  }
  if (!res.ok) throw new LinkRefused(res.status)
  const body = (await res.json()) as { url?: string; expiresInSec?: number }
  if (!body.url) throw new LinkRefused(502)
  const url = /^https?:\/\//i.test(body.url) ? body.url : `${API()}${body.url.startsWith('/') ? '' : '/'}${body.url}`
  const ttl = Number(body.expiresInSec)
  return { url, expiresAt: Number.isFinite(ttl) && ttl > 0 ? deps.now() + ttl * 1000 : null }
}

/**
 * A URL that opens `url`: a signed link for a private attachment, the URL
 * itself otherwise. Throws when the backend refuses (not a member, signed out).
 *
 * A chat file that was just sent may not have reached the server that knows
 * its conversation yet, which reads as "not found": asked again a moment later.
 */
export async function resolveAttachmentUrl(
  url: string,
  opts: ResolveOptions = {},
  deps: ResolveDeps = {},
): Promise<string> {
  const ref = privateRef(url)
  if (!ref) return url
  const d: Required<ResolveDeps> = {
    getWithAppToken: deps.getWithAppToken ?? defaultGetWithAppToken,
    getWithRoomToken: deps.getWithRoomToken ?? defaultGetWithRoomToken,
    now: deps.now ?? Date.now,
    sleep: deps.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms))),
  }

  const key = opts.meetingId ? `${url}#${opts.meetingId}` : url
  const hit = cache.get(key)
  if (hit && (hit.expiresAt === null || hit.expiresAt - d.now() > RENEW_BEFORE_MS)) return hit.url

  const pending = inflight.get(key)
  if (pending) return pending

  const run = (async () => {
    for (let attempt = 0; ; attempt++) {
      try {
        const got = await ask(ref, opts, d)
        cache.set(key, got)
        return got.url
      } catch (err) {
        const notYet = err instanceof LinkRefused && err.status === 404 && ref.kind === 'chat'
        if (!notYet || attempt >= NOT_YET_RETRIES_MS.length) throw err
        await d.sleep(NOT_YET_RETRIES_MS[attempt])
      }
    }
  })()
  inflight.set(key, run)
  try {
    return await run
  } finally {
    inflight.delete(key)
  }
}

/**
 * Open an attachment in a new tab, as a link would.
 *
 * A private one needs its link fetched first, and a tab opened after an await
 * is no longer the user's click: the browser blocks it as a popup. So the tab
 * opens blank inside the click and is pointed at the link once it arrives, or
 * closed if it is refused. Any other URL opens directly.
 */
export function openAttachmentInTab(
  url: string,
  opts: ResolveOptions = {},
  deps: {
    open?: (url: string, features?: string) => Window | null
    resolve?: (url: string, opts: ResolveOptions) => Promise<string>
  } = {},
): void {
  const open = deps.open ?? ((u: string, features?: string) => window.open(u, '_blank', features))
  if (!isPrivateAttachmentUrl(url)) {
    open(url, 'noopener,noreferrer')
    return
  }
  const tab = open('about:blank')
  if (!tab) return
  tab.opener = null
  ;(deps.resolve ?? resolveAttachmentUrl)(url, opts).then(
    (signed) => { tab.location.href = signed },
    () => tab.close(),
  )
}
